// ─────────────────────────────────────────
// i18n/detector.ts — Locale detection logic
// Priority: URL param > escolha guardada > hostname TLD > browser
// Tauvior — IA Edge-first
// ─────────────────────────────────────────

import type { Locale } from './types';

/** All supported locales */
const SUPPORTED_LOCALES: Locale[] = ['pt-BR', 'en-US'];
const DEFAULT_LOCALE: Locale = 'pt-BR';

/** Onde a escolha explícita do visitante fica guardada, por origem */
const STORAGE_KEY = 'tauvior:locale';

/**
 * Reads the `?lang=` query parameter from the URL.
 * Useful for previewing a locale without changing the domain.
 * e.g. tauvior.com.br?lang=en-US
 */
function fromQueryParam(): Locale | null {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get('lang') as Locale | null;
  if (lang && SUPPORTED_LOCALES.includes(lang)) return lang;
  return null;
}

/**
 * Lê a escolha que o visitante já fez nesta origem.
 *
 * Sem isso o site é multipágina e o idioma não sobrevive ao primeiro
 * clique: o `?lang=` só existe na URL onde o seletor foi acionado, e
 * qualquer link do menu, do rodapé ou dos cards voltaria a cair no TLD.
 */
function fromStorage(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
    return stored && SUPPORTED_LOCALES.includes(stored) ? stored : null;
  } catch {
    return null; // modo privado / storage bloqueado
  }
}

/**
 * Guarda a escolha do visitante. Só o `?lang=` chama isto: idioma
 * inferido de TLD ou de navegador é palpite, e palpite não vira decisão
 * gravada — senão a primeira visita fixaria o idioma para sempre.
 */
export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Sem storage o idioma ainda funciona na página atual, só não persiste
  }
}

/**
 * Infers locale from the current hostname TLD.
 *
 * Rules:
 *   *.com.br  → pt-BR
 *   *.br      → pt-BR
 *   *.com     → pt-BR
 *   localhost → pt-BR (default for local dev, override with ?lang=)
 */
function fromHostname(): Locale | null {
  const host = window.location.hostname.toLowerCase();

  if (host === 'localhost' || host === '127.0.0.1') return null;

  if (host.endsWith('.com.br') || host.endsWith('.br')) return 'pt-BR';
  if (host.endsWith('.com'))                             return 'pt-BR';

  return null;
}

/**
 * Reads the browser's preferred languages and maps the first match
 * to a supported locale.
 */
function fromBrowser(): Locale | null {
  const preferred = navigator.languages ?? [navigator.language];

  for (const lang of preferred) {
    if (lang.startsWith('pt')) return 'pt-BR';
    if (lang.startsWith('en')) return 'en-US';
  }

  return null;
}

/**
 * Repassa o `?lang=` da URL atual para um link interno.
 *
 * É a rede de segurança para quando o storage está bloqueado (navegação
 * privada, política corporativa): sem ela o idioma escolhido morreria no
 * primeiro clique. De quebra, enquanto se navega fora do idioma padrão a
 * URL carrega o idioma — copiar um link no meio da sessão compartilha a
 * página no idioma que se está lendo.
 *
 * Só age quando o parâmetro já existe: navegação no idioma padrão continua
 * com URLs limpas.
 */
export function withLocaleParam(href: string): string {
  const current = new URLSearchParams(window.location.search).get('lang');
  if (!current) return href;

  // Âncora pura fica na mesma página; externo e mailto não são nossos
  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return href;
  }

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return href;

    url.searchParams.set('lang', current);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

/**
 * Resolves the active locale using the priority chain:
 *   1. ?lang= query param   (escolha explícita — e fica guardada)
 *   2. Escolha guardada     (mantém o idioma pela navegação inteira)
 *   3. Hostname TLD         (production domain routing)
 *   4. Browser language     (user preference fallback)
 *   5. DEFAULT_LOCALE       (pt-BR)
 *
 * A escolha guardada vem antes do TLD de propósito: o domínio é o padrão
 * de quem chega, mas quem trocou de idioma no seletor decidiu — e a
 * decisão não pode ser desfeita pelo domínio a cada página.
 */
export function detectLocale(): Locale {
  const chosen = fromQueryParam();
  if (chosen) {
    rememberLocale(chosen);
    return chosen;
  }

  return (
    fromStorage()  ??
    fromHostname() ??
    fromBrowser()  ??
    DEFAULT_LOCALE
  );
}
